import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, IdentificationCard, Bank, FileText, WarningCircle, DeviceMobile, Robot, ShieldCheck } from "@phosphor-icons/react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

const STEPS = ["incomplete", "verification_pending", "agreement_pending", "admin_approval_pending", "approved"];
const BIZ = ["Sole Proprietor", "Partnership", "Private Limited", "LLP", "Trust / NGO"];
const REC_STYLES = { approve: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200", review: "border-amber-500/30 bg-amber-500/10 text-amber-200", reject: "border-red-500/30 bg-red-500/10 text-red-200" };

export default function Onboarding() {
  const [profile, setProfile] = useState(null);
  const [payout, setPayout] = useState({});
  const [agreement, setAgreement] = useState(null);
  const [form, setForm] = useState({});
  const [signature, setSignature] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);

  // mobile OTP
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const load = async () => {
    const [p, po, ag] = await Promise.all([
      api.get("/organizer/profile"), api.get("/organizer/payout"), api.get("/organizer/agreement"),
    ]);
    setProfile(p.data); setForm(p.data); setPayout(po.data || {}); setAgreement(ag.data);
    if (p.data.mobile) setMobile(p.data.mobile);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const stepIdx = STEPS.indexOf(profile?.status);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const sendOtp = async () => {
    try { const { data } = await api.post("/organizer/onboarding/mobile/request", { mobile }); setOtpSent(true); toast.success(data.message); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const verifyOtp = async () => {
    try { const { data } = await api.post("/organizer/onboarding/mobile/verify", { mobile, otp }); toast.success(data.message); setOtpSent(false); setOtp(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveKyc = async () => {
    try { await api.put("/organizer/kyc", form); toast.success("KYC details saved."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitKyc = async () => {
    try { await api.put("/organizer/kyc", form); const { data } = await api.post("/organizer/kyc/submit"); toast.success(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const savePayout = async () => {
    try { await api.put("/organizer/payout", payout); toast.success("Payout account verified."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sign = async () => {
    try { const { data } = await api.post("/organizer/agreement/sign", { signature, accepted }); toast.success(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <PageLoader />;
  const approved = profile.status === "approved";
  const mobileVerified = !!profile.mobile_verified;
  const review = profile.ai_review;

  return (
    <div className="max-w-3xl">
      <div className="overline mb-1">Organizer onboarding</div>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Get verified to go live</h1>
        <StatusBadge status={profile.status} />
      </div>

      <div className="flex items-center gap-1 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-primary" : "bg-muted"}`} />
        ))}
      </div>

      {approved && (
        <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 flex items-start gap-3" data-testid="approved-banner">
          <CheckCircle weight="fill" className="text-emerald-400 mt-0.5" size={22} />
          <div>
            <div className="font-semibold">You're approved! 🎉</div>
            <p className="text-sm text-muted-foreground">You can now publish events and accept bookings.</p>
            <Button asChild className="mt-3 rounded-full" size="sm"><Link to="/organizer/events/new">Create your first event <ArrowRight className="ml-1.5" /></Link></Button>
          </div>
        </div>
      )}

      {profile.admin_note && ["rejected", "correction_required"].includes(profile.status) && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-2 text-sm">
          <WarningCircle className="text-amber-400 mt-0.5" size={18} /> <span><b>Admin note:</b> {profile.admin_note}</span>
        </div>
      )}

      {/* AI onboarding review */}
      {review && (
        <div className={`mb-6 rounded-xl border p-4 ${REC_STYLES[review.recommendation] || "border-border bg-card"}`} data-testid="ai-review-banner">
          <div className="flex items-center gap-2 font-semibold text-sm"><Robot size={18} weight="fill" /> AI onboarding review · <span className="capitalize">{review.recommendation}</span>{typeof review.score === "number" ? ` (${review.score}% confidence)` : ""}</div>
          {review.reasons?.length > 0 && <ul className="mt-2 text-sm list-disc pl-5 space-y-0.5">{review.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          <p className="text-xs mt-2 opacity-80">This automated assessment assists the admin team with your final approval.</p>
        </div>
      )}

      {/* Mobile verification */}
      <Section icon={DeviceMobile} title="1. Verify mobile (+91)" done={mobileVerified}>
        {mobileVerified ? (
          <p className="text-sm text-emerald-400 inline-flex items-center gap-1" data-testid="mobile-verified"><CheckCircle weight="fill" /> {profile.mobile} verified</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">A verified Indian mobile number is required before you submit KYC.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+919876543210" className="sm:max-w-xs" data-testid="onboarding-mobile" disabled={otpSent} />
              {!otpSent ? (
                <Button onClick={sendOtp} className="rounded-full" data-testid="send-otp">Send OTP</Button>
              ) : (
                <>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" className="sm:max-w-[140px]" data-testid="onboarding-otp" />
                  <Button onClick={verifyOtp} className="rounded-full" data-testid="verify-otp">Verify</Button>
                </>
              )}
            </div>
          </div>
        )}
      </Section>

      {/* KYC */}
      <Section icon={IdentificationCard} title="2. Business KYC (PAN + GST)" done={stepIdx >= 1}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Company / Business name"><Input value={form.company_name || ""} onChange={set("company_name")} data-testid="kyc-company" /></Field>
          <Field label="Business type">
            <Select value={form.business_type || ""} onValueChange={set("business_type")}>
              <SelectTrigger data-testid="kyc-biztype"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{BIZ.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="GST number"><Input value={form.gst_number || ""} onChange={set("gst_number")} placeholder="24AAACR1234A1Z5" data-testid="kyc-gst" /></Field>
          <Field label="PAN number"><Input value={form.pan_number || ""} onChange={set("pan_number")} placeholder="AAACR1234A" data-testid="kyc-pan" /></Field>
          <Field label="Authorized signatory"><Input value={form.authorized_signatory || ""} onChange={set("authorized_signatory")} data-testid="kyc-signatory" /></Field>
          <Field label="Contact phone"><Input value={form.contact_phone || ""} onChange={set("contact_phone")} /></Field>
          <div className="sm:col-span-2"><Field label="Registered address"><Textarea value={form.registered_address || ""} onChange={set("registered_address")} rows={2} /></Field></div>
        </div>
        {profile.gst_verified && <p className="text-xs text-emerald-400 mt-3 inline-flex items-center gap-1"><CheckCircle weight="fill" /> GST, PAN & business verified</p>}
        {!approved && (
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={saveKyc} className="rounded-full" data-testid="save-kyc">Save draft</Button>
            {stepIdx < 1 && <Button onClick={submitKyc} disabled={!mobileVerified} className="rounded-full" data-testid="submit-kyc">Submit for verification</Button>}
          </div>
        )}
        {stepIdx < 1 && !mobileVerified && <p className="text-xs text-muted-foreground mt-2">Verify your mobile number first.</p>}
      </Section>

      {/* Payout */}
      <Section icon={Bank} title="3. Payout account" done={payout.status === "verified"}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Account holder"><Input value={payout.account_holder || ""} onChange={(e) => setPayout({ ...payout, account_holder: e.target.value })} data-testid="payout-holder" /></Field>
          <Field label="Account number"><Input value={payout.account_number && payout.account_number !== "stored_securely" ? payout.account_number : ""} onChange={(e) => setPayout({ ...payout, account_number: e.target.value })} placeholder={payout.account_number_masked || ""} data-testid="payout-number" /></Field>
          <Field label="IFSC"><Input value={payout.ifsc || ""} onChange={(e) => setPayout({ ...payout, ifsc: e.target.value })} data-testid="payout-ifsc" /></Field>
          <Field label="Bank name"><Input value={payout.bank_name || ""} onChange={(e) => setPayout({ ...payout, bank_name: e.target.value })} data-testid="payout-bank" /></Field>
        </div>
        <Button onClick={savePayout} className="rounded-full mt-4" variant="outline" data-testid="save-payout">Save & verify payout</Button>
      </Section>

      {/* Agreement */}
      <Section icon={FileText} title="4. Organizer agreement" done={!!agreement?.signed}>
        {agreement?.signed ? (
          <p className="text-sm text-emerald-400 inline-flex items-center gap-1"><CheckCircle weight="fill" /> Signed v{agreement.signed.version} by {agreement.signed.signatory} on {new Date(agreement.signed.signed_at).toLocaleDateString("en-IN")}</p>
        ) : (
          <>
            <div className="rounded-lg border border-border bg-muted/40 p-4 max-h-56 overflow-y-auto text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1"
              dangerouslySetInnerHTML={{ __html: agreement?.template?.content || "" }} />
            <label className="flex items-center gap-2 mt-4 text-sm">
              <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="agree-checkbox" /> I have read and accept the agreement
            </label>
            <div className="mt-3"><Label>Digital signature (type your full name)</Label><Input value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1.5 max-w-sm" data-testid="signature-input" /></div>
            <Button onClick={sign} disabled={!accepted || !signature || stepIdx < 2} className="rounded-full mt-4" data-testid="sign-agreement">Accept & sign</Button>
            {stepIdx < 2 && <p className="text-xs text-muted-foreground mt-2">Complete KYC verification first.</p>}
          </>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, done, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <span className={`w-9 h-9 grid place-items-center rounded-lg ${done ? "bg-emerald-500/15 text-emerald-300" : "bg-muted text-muted-foreground"}`}>{done ? <CheckCircle weight="fill" size={18} /> : <Icon size={18} />}</span>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) { return <div><Label className="mb-1.5 block">{label}</Label>{children}</div>; }
